import React, { useContext, useEffect, useMemo } from 'react';

import { useQuery } from '@apollo/react-hooks';
import { DialogContent, Modal } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { Route, Switch, useLocation } from 'react-router-dom';

import { Footer } from '../../components/Footer';
import { Header } from '../../components/Header';
import LinkedInModal from '../../components/LinkedInModal';
import { Loader } from '../../components/Loader';
import { NewsItem } from '../../components/NewsItem';
import { NewsList } from '../../components/NewsList';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import RouteWithCode from '../../components/RouteWithCode/RouteWithCode';
import { ShareModal } from '../../components/ShareModal';
import { VideoModal } from '../../components/VideoModal';
import { ScriptsContext } from '../../context/ScriptsContext';
import { TestContext } from '../../context/TestContext';
import { GET_COURSES_EXIST_INFO, GET_COURSES_PROGRESS_INFO } from '../../graphql/courses';
import { GET_HAS_NEWS } from '../../graphql/local';
import { GET_NAVIGATION } from '../../graphql/navigation';
import { GET_LIST_PARTNERS_BY_FILTER } from '../../graphql/partners';
import { AnalyticsPage } from '../../pages/AnalyticsPage';
import { AssignedLearningPage } from '../../pages/AssignedLearningPage';
import { CoursesListPage } from '../../pages/CourcesListPage';
import { CourseDetailsPage } from '../../pages/CourseDetailsPage';
import { CourseProgress } from '../../pages/CourseProgress';
import { CourseSharePage } from '../../pages/CourseSharePage';
import { DashboardPage } from '../../pages/DashboardPage';
import { DiscoverPage } from '../../pages/DiscoverPage';
import { DEFAULT_URL } from '../../pages/DiscoverPage/const';
import { Homepage } from '../../pages/Homepage';
import { LoginPage } from '../../pages/LoginPage';
import { LogoutPage } from '../../pages/LogoutPage';
import { MyAccountPage } from '../../pages/MyAccountPage';
import NotFoundPage from '../../pages/NotFoundPage';
import { PathwayPage } from '../../pages/PathwayPage';
import { PricePage } from '../../pages/PricePage';
import { RegistrationPage } from '../../pages/RegistrationPage';
import { ResetPasswordPage } from '../../pages/ResetPasswordPage';
import { SharePage } from '../../pages/SharePage';
import { SingleVideoPage } from '../../pages/SingleVideoPage';
import { SsoLoginPage } from '../../pages/SsoLoginPage';
import { TestPage } from '../../pages/TestPage';
import ThankYouPage from '../../pages/ThankYouPage';
import {
  Tenant,
  LearnerInfo,
  NavigationType,
  Query,
  CourseDropdownItem,
  COURSE_STATUSES,
} from '../../types';
import { useAuth } from '../../utils/hooks';
import { CustomPageLayout } from '../CustomPageLayout';
import useStyles from './styles';

interface RXDLayoutProps {
  tenant: Tenant;
  userInfo: LearnerInfo;
  userLoading: boolean;
  authAppId?: string;
  waitForUser?: boolean;
  loadingTenant: boolean;
}

export const Layout: React.FC<RXDLayoutProps> = ({
  tenant,
  loadingTenant,
  userInfo,
  userLoading,
  authAppId,
  waitForUser,
}) => {
  const classes = useStyles();
  const location = useLocation();
  const pathname = location?.pathname;
  const { isLoadedAnalyticScript } = useContext(ScriptsContext);
  const {
    openShareModal,
    openLinkedInModal,
    handleCloseLinkedInModal,
    handleCloseShareModal,
    testResults,
  } = useContext(TestContext);

  const { isAuthenticated } = useAuth();
  const logoUrl = userInfo?.company?.logo_url ? userInfo?.company?.logo_url : tenant?.logo_url;
  const partnerTypeId = tenant?.partner_type_id;
  const tenantTitle = tenant?.title;
  const tenantName = tenant?.name;
  const tenantId = tenant?.id;
  const tenantCircleUrl = tenant?.circle_image_url;
  const linkedInId = tenant?.linkedin_id;
  const assignedLearningsName = userInfo?.company?.short_name || '';

  const {
    data: listPartners,
    // loading: partnersCompanyLoading,
    // error: partnersCompanyError
  } = useQuery(GET_LIST_PARTNERS_BY_FILTER, {
    variables: {
      filter: {
        type_id: partnerTypeId,
      },
    },
  });

  const { data } = useQuery(GET_HAS_NEWS);
  const hasNews = data?.hasNews;

  const footerPartners = listPartners?.listPartners;

  const footerNavigationType = isAuthenticated
    ? NavigationType.FooterLoggedIn
    : NavigationType.FooterLoggedOut;
  const { data: navigationData } = useQuery(GET_NAVIGATION, {
    variables: {
      filter: {
        navigation_type: isAuthenticated
          ? [footerNavigationType]
          : [NavigationType.LogedOut, footerNavigationType],
      },
    },
    skip: !tenant,
  });

  const navigation = navigationData?.getNavigation;
  const headerNavigation = navigation?.find(
    (nav) => nav.navigation_type === NavigationType.LogedOut,
  );
  const footerNavigation = navigation?.find((nav) => nav.navigation_type === footerNavigationType);

  const { data: coursesExistData } = useQuery<Pick<Query, 'listCoursesLogedOut'>>(
    GET_COURSES_EXIST_INFO,
  );

  const { data: coursesProgressData, refetch: refetchCourseProgressData } = useQuery<
    Pick<Query, 'listCoursesLogedIn'>
  >(GET_COURSES_PROGRESS_INFO, {
    skip: !isAuthenticated,
  });

  const coursesProgress = coursesProgressData?.listCoursesLogedIn || [];

  const hasAnotherCourses = useMemo(
    () =>
      coursesExistData?.listCoursesLogedOut && coursesExistData?.listCoursesLogedOut?.length > 1,
    [coursesExistData],
  );

  const dropdownCourses: CourseDropdownItem[] = useMemo(() => {
    if (process.env.REACT_APP_BUILD_TYPE !== 'prod') {
      const coursesList = coursesExistData?.listCoursesLogedOut;
      const coursesProgress = coursesProgressData?.listCoursesLogedIn;

      if (coursesList) {
        return coursesList.map((item) => ({
          id: item?.id,
          name: item?.name,
          slug: item?.slug,
          navigationDescription: item?.navigation_description,
          isStarted: Boolean(
            coursesProgress?.find(
              (progressItem) =>
                item?.id === progressItem?.id &&
                progressItem?.status &&
                progressItem.status !== COURSE_STATUSES.NOT_STARTED,
            ),
          ),
        }));
      }
    }

    return [];
  }, [coursesExistData, coursesProgressData]);

  useEffect(() => {
    if (isLoadedAnalyticScript && userInfo && window?.verseEvents?.changeUserId) {
      window.verseEvents.changeUserId(userInfo?.id);
    }
  }, [userInfo, isLoadedAnalyticScript]);

  return (
    <>
      <Header
        navigation={headerNavigation}
        assignedLearningsName={assignedLearningsName}
        logoUrl={logoUrl}
        userInfo={userInfo}
        userLoading={userLoading}
        dropdownCourses={dropdownCourses}
      />
      <Box className={classes.root}>
        <Switch>
          <Route
            path="/login/:ssoKey"
            render={(props) => (
              <SsoLoginPage
                {...props}
                userInfo={userInfo}
                userLoading={userLoading}
                tenant={tenant}
                loadingTenant={loadingTenant}
              />
            )}
          />
          <Route
            exact
            path="/login"
            render={(props) => (
              <LoginPage {...props} userInfo={userInfo} userLoading={userLoading} />
            )}
          />
          <Route
            path="/logout"
            render={(props) => <LogoutPage {...props} loadingUser={userLoading} />}
          />
          <Route path="/registration" render={(props) => <RegistrationPage {...props} />} />
          <Route path="/forgot-password" render={(props) => <ResetPasswordPage {...props} />} />
          {hasNews && (
            <Route exact path="/news/:id">
              <NewsItem fallback={<Loader />} />
            </Route>
          )}
          {hasNews && (
            <Route exact path="/news">
              <NewsList fallback={<Loader />} />
            </Route>
          )}
          <Route exact path="/pricing">
            <PricePage
              fallback={<Loader />}
              userInfo={userInfo}
              userLoading={userLoading}
              tenantTitle={tenantTitle}
            />
          </Route>
          <ProtectedRoute
            exact
            withSubscription={false}
            path="/payment-successful"
            userInfo={userInfo}
            userLoading={userLoading}
            render={(props) => <ThankYouPage tenantLinkedInId={linkedInId} {...props} />}
          />
          <ProtectedRoute
            exact={false}
            path="/analytics"
            userInfo={userInfo}
            userLoading={userLoading}
            render={(props) => (
              <AnalyticsPage
                userInfo={userInfo}
                tenantTitle={tenantTitle}
                {...props}
                fallback={<Loader />}
              />
            )}
          />
          <ProtectedRoute
            exact={false}
            path="/assigned-learning"
            userInfo={userInfo}
            userLoading={userLoading}
            render={(props) => (
              <AssignedLearningPage
                tenantTitle={tenantTitle}
                tenantName={tenantName}
                title={assignedLearningsName}
                userInfo={userInfo}
                fallback={<Loader />}
                {...props}
              />
            )}
          />
          <ProtectedRoute
            exact
            path={['/home', '/recently-watched-my-watchlist/']}
            userInfo={userInfo}
            userLoading={userLoading}
            render={(props) => (
              <DashboardPage
                userInfo={userInfo}
                tenantTitle={tenantTitle}
                {...props}
                fallback={<Loader />}
              />
            )}
          />
          <Route
            exact
            path="/videos/:slug"
            render={(props) => (
              <SingleVideoPage
                tenantTitle={tenantTitle}
                userInfo={userInfo}
                waitForUser={waitForUser}
                fallback={<Loader />}
                bumperUrl={tenant?.video_bumper_url}
                {...props}
              />
            )}
          />
          <ProtectedRoute
            exact
            userInfo={userInfo}
            userLoading={userLoading}
            path={['/pathway/:slug/test/:testId', `${DEFAULT_URL}/pathways/:slug/test/:testId`]}
            render={(props) => (
              <TestPage
                linkedInId={linkedInId}
                tenantTitle={tenantTitle}
                tenantName={tenantName}
                tenantCircleUrl={tenantCircleUrl}
                tenantId={tenantId}
                userId={userInfo?.id}
                fallback={<Loader />}
                {...props}
              />
            )}
          />
          <Route
            exact
            path={['/pathway/:slug', `${DEFAULT_URL}/pathways/:slug`]}
            render={(props) => (
              <PathwayPage
                linkedInId={linkedInId}
                tenantCircleUrl={tenantCircleUrl}
                userInfo={userInfo}
                tenantTitle={tenantTitle}
                tenantName={tenantName}
                waitForUser={waitForUser}
                fallback={<Loader />}
                {...props}
              />
            )}
          />
          <Route
            exact
            path="/share/courses/:hash"
            render={(props) => (
              <CourseSharePage
                tenantTitle={tenantTitle}
                tenantName={tenantName}
                {...props}
                fallback={<Loader />}
              />
            )}
          />
          <Route
            exact
            path="/share/:hash"
            render={(props) => (
              <SharePage
                tenantTitle={tenantTitle}
                tenantName={tenantName}
                {...props}
                fallback={<Loader />}
              />
            )}
          />
          <Route
            exact={false}
            path={[DEFAULT_URL, '/pathways', '/videos', '/experts', '/expert/:expertSlug']}
            render={(props) => (
              <DiscoverPage
                tenant={tenant}
                userInfo={userInfo}
                userLoading={userLoading}
                fallback={<Loader />}
                {...props}
              />
            )}
          />
          <Route
            exact
            path="/courses/:slug"
            render={(props) => (
              <CourseDetailsPage
                {...props}
                userInfo={userInfo}
                tenant={tenant}
                coursesProgress={coursesProgress}
                hasAnotherCourses={hasAnotherCourses}
                refetchCourseProgressData={refetchCourseProgressData}
              />
            )}
          />
          <ProtectedRoute
            exact
            path="/courses/:slug/progress"
            userInfo={userInfo}
            userLoading={userLoading}
            render={(props) => (
              <CourseProgress
                {...props}
                userInfo={userInfo}
                tenant={tenant}
                hasAnotherCourses={hasAnotherCourses}
                fallback={<Loader />}
              />
            )}
          />
          <Route
            exact
            path="/courses"
            render={(props) => <CoursesListPage tenant={tenant} {...props} />}
          />
          <RouteWithCode exact path="/404" code={404}>
            <NotFoundPage />
          </RouteWithCode>
          <ProtectedRoute
            exact={false}
            path="/my-account"
            userInfo={userInfo}
            userLoading={userLoading}
            withSubscription={false}
            render={(props) => (
              <MyAccountPage {...props} tenant={tenant} userInfo={userInfo} authAppId={authAppId} />
            )}
          />
          <Route
            exact
            path="/"
            render={(props) => (
              <Homepage {...props} userInfo={userInfo} userLoading={userLoading} tenant={tenant} />
            )}
          />
          <Route exact path={pathname}>
            <CustomPageLayout userInfo={userInfo} tenant={tenant} />
          </Route>
        </Switch>
      </Box>

      <VideoModal />
      <Modal open={openShareModal} className={classes.modal}>
        <DialogContent className={classes.dialog}>
          <ShareModal
            entityName={testResults?.pathwayName}
            cpdCredits={testResults?.cpdCredits}
            resultHash={testResults?.resultHash}
            category={testResults?.category}
            slug={testResults?.pathwaySlug}
            handleClose={handleCloseShareModal}
            tenantName={tenantName}
            type={testResults?.type}
            fbName={tenant?.fb_name}
            twitterHandle={tenant?.twitter_handle}
            twitterHashtags={tenant?.twitter_hashtags_array}
          />
        </DialogContent>
      </Modal>
      <Modal open={openLinkedInModal} className={classes.modal}>
        <DialogContent className={classes.dialog}>
          <LinkedInModal
            pathwayName={testResults?.pathwayName}
            passDate={testResults?.passDate}
            linkedInId={linkedInId}
            resultHash={testResults?.resultHash}
            handleClose={handleCloseLinkedInModal}
            certUrl={testResults?.certUrl}
          />
        </DialogContent>
      </Modal>

      <Footer
        footerPartners={footerPartners}
        navigation={footerNavigation}
        tenantTitle={tenantTitle}
        linkedinLink={tenant?.linkedin_link}
        twitterLink={tenant?.twitter_link}
        facebookLink={tenant?.facebook_link}
        instagramLink={tenant?.instagram_link}
      />
    </>
  );
};

export default Layout;
